import { useEffect, useState } from 'react';
import { ApiError, type Api, type User } from '../core/api';
import { t, tError } from '../core/i18n';
import { platform } from '../core/platform';
import { Logo } from './Logo';

export function LoginPage({ api, onLogin }: { api: Api; onLogin: (u: User) => void }) {
  const [register, setRegister] = useState(false);
  const [canRegister, setCanRegister] = useState(false);
  const [licenseRequired, setLicenseRequired] = useState(false);
  const [licenseKey, setLicenseKey] = useState('');
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);
  const [server, setServer] = useState(api.base);
  const needsServer = platform().needsServerUrl;

  useEffect(() => {
    if (needsServer && !api.base) return;
    api
      .config()
      .then((c) => {
        setCanRegister(c.allowRegistration);
        setLicenseRequired(!!c.licenseRequired);
      })
      .catch(() => {});
  }, [api, needsServer]);

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setBusy(true);
    try {
      if (needsServer) api.setServer(server);
      onLogin(await api.login(username.trim(), password, register, licenseKey.trim()));
    } catch (err) {
      setError(err instanceof ApiError ? tError(err.message) : t('errorGeneric'));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="login">
      <form className="login-form" onSubmit={submit}>
        <div className="brand">
          <Logo size={24} />
          {t('appShort')}
        </div>
        <h1>{register ? t('registerTitle') : t('signInTitle')}</h1>
        {needsServer && (
          <>
            <label className="label" htmlFor="server">
              {t('serverUrl')}
            </label>
            <input id="server" className="input" value={server} placeholder="https://cloud.example.com" onChange={(e) => setServer(e.target.value)} inputMode="url" autoCapitalize="off" spellCheck={false} required />
          </>
        )}
        <label className="label" htmlFor="user">
          {t('username')}
        </label>
        <input id="user" className="input" value={username} onChange={(e) => setUsername(e.target.value)} autoComplete="username" autoCapitalize="off" spellCheck={false} required />
        <label className="label" htmlFor="pw">
          {t('password')}
        </label>
        <input
          id="pw"
          className="input"
          type="password"
          value={password}
          onChange={(e) => setPassword(e.target.value)}
          autoComplete={register ? 'new-password' : 'current-password'}
          minLength={register ? 6 : undefined}
          required
        />
        {register && licenseRequired && (
          <>
            <label className="label" htmlFor="license">
              {t('licenseKey')}
            </label>
            <input
              id="license"
              className="input"
              value={licenseKey}
              onChange={(e) => setLicenseKey(e.target.value)}
              placeholder="XXXX-XXXX-XXXX-XXXX"
              autoComplete="off"
              autoCapitalize="characters"
              spellCheck={false}
              required
            />
            <div className="muted" style={{ fontSize: 12, marginTop: 6 }}>
              {t('licenseKeyHint')}
            </div>
          </>
        )}
        {error && <div className="form-error">{error}</div>}
        <button className="btn primary block" disabled={busy}>
          {busy ? <span className="spinner" /> : register ? t('register') : t('login')}
        </button>
        {(canRegister || register) && (
          <div className="switch-mode">
            {register ? t('haveAccount') : t('noAccount')}{' '}
            <button type="button" className="link-btn" onClick={() => setRegister(!register)}>
              {register ? t('login') : t('register')}
            </button>
          </div>
        )}
      </form>
    </div>
  );
}
